import type {Schema} from 'platform/components';

import {useEffect, useState} from 'react';

import type {Nullish} from 'shared/web';

import {useHttpCalls} from '../api/useHttpCalls';

type UseFilterSchemaParams = {
  resourceId: string | Nullish;
  recordId: string | Nullish;
  enabled: boolean;
};

/**
 * Fetches the BE filter schema (`GET /v2/model-schema/datagrid-{gridCode}`) used to
 * auto-render an external DynamicUi filter form when no `filterComponent` is supplied.
 * Enabled only when the grid has an `externalFilterId` and no `filterComponent`.
 */
export function useFilterSchema(params: UseFilterSchemaParams): {schema: Schema | null} {
  const http = useHttpCalls();
  const [schema, setSchema] = useState<Schema | null>(null);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- one-shot async schema load; eag-grid forbids bare useEffect, this is the sanctioned data-fetch exception (mirrors ExternalFiltersPortal's DOM-read effect). http is intentionally omitted from deps (new identity each render; reads gridCode from the store).
  useEffect(
    function fetchFilterSchema() {
      if (!params.enabled) {
        setSchema(null);
        return;
      }

      let cancelled = false;

      http
        .getFilterSchema({
          externalFilterResourceId: params.resourceId,
          externalFilterRecordId: params.recordId,
        })
        .then((response) => {
          if (!cancelled) {
            setSchema(response?.root ?? null);
          }
        });

      return () => {
        cancelled = true;
      };
    },
    // eslint-disable-next-line react/exhaustive-deps -- see comment above re: http identity
    [params.enabled, params.resourceId, params.recordId]
  );

  return {schema};
}
